import { Box, Card, CardContent, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, type JSX } from "react";

import type { OttoBot } from "../types.js";
import { otSrcOrigin } from "./otSrcOrigin.js";
import { Pill } from "./Pill.js";

/**
 * One Otto bot card — name/slug, enabled state, attribute pills, a
 * collapsible embed snippet, and a soft-disable link. Extracted from
 * `BotsPanel.tsx`.
 */
export function BotCard({
  bot,
  onSoftDelete,
}: {
  bot: OttoBot;
  onSoftDelete: () => void;
}): JSX.Element {
  const [showSnippet, setShowSnippet] = useState(false);
  return (
    <Card>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: "text.primary" }}>{bot.display_name}</Typography>
            <Typography sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>{bot.slug}</Typography>
          </Box>
          <Box
            sx={{
              borderRadius: 0.5,
              px: 1,
              py: 0.25,
              fontSize: 12,
              color: bot.enabled ? "success.main" : "text.disabled",
              bgcolor: (t) => (bot.enabled ? alpha(t.palette.success.main, 0.1) : t.palette.surfaceSoft),
            }}
          >
            {bot.enabled ? "enabled" : "disabled"}
          </Box>
        </Box>

        {bot.description && (
          <Typography sx={{ mt: 1, fontSize: 14, color: "text.disabled" }}>{bot.description}</Typography>
        )}

        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Pill>{bot.persona}</Pill>
          <Pill>{bot.allowed_source_apps.length || "any"} sources</Pill>
          <Pill>{Object.keys(bot.metadata_filters).length || 0} filters</Pill>
          {bot.anonymous_allowed && <Pill tone="warn">anonymous</Pill>}
          {!bot.enable_clarifying_questions && <Pill tone="mute">no clarifying</Pill>}
        </Box>

        <Box
          component="details"
          sx={{ mt: 1.5 }}
          open={showSnippet}
          onToggle={(e: React.SyntheticEvent<HTMLDetailsElement>) => setShowSnippet(e.currentTarget.open)}
        >
          <Box
            component="summary"
            sx={{ cursor: "pointer", fontSize: 12, color: "primary.main", "&:hover": { textDecoration: "underline" } }}
          >
            Embed snippet
          </Box>
          <Box
            component="pre"
            sx={{ mt: 1, overflowX: "auto", borderRadius: 0.5, bgcolor: "surfaceSoft", p: 1, fontSize: 12 }}
          >
{`<script
  src="${otSrcOrigin()}/embed.js?bot=${bot.embed_key}"
  data-token-endpoint="/api/me/otto-token"
  async></script>`}
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Link
            component="button"
            type="button"
            onClick={onSoftDelete}
            underline="hover"
            sx={{ fontSize: 12, color: "error.main" }}
          >
            Disable bot
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}
